import React, { useState } from 'react';
import Image from 'next/image';
import { PromoFlavorCard as PromoFlavorCardProps, SanityKeyed } from 'types/studio/studio';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ButtonVanilla,
  Tag,
  Typography,
  getImageSizes
} from '@ag1/ui';
import { urlFor } from '@portal/shared/utils/urlFor';
import { vars } from '@ag1/ui/theme';
import { getSanityLinkUrl } from '@portal/shared/utils/getSanityLinkUrl';
import { useLocale } from 'shared/hooks/useLocale';
import clsx from 'clsx';
import CustomPortableText from 'shared/studio/PortableText/PortableText';
import { CommonSectionProps } from '../types';
import { styles, cardContainer } from './PromoFlavorCard.css';
import { FlavorBubbleButton } from '../../components/FlavorBubbleButton';

const { colors, layoutSpacing } = vars;

const PromoFlavorCard = (props: SanityKeyed<PromoFlavorCardProps> & CommonSectionProps) => {
  const { _key: key, flavorCard = [], bannerWidth } = props;

  const locale = useLocale();
  const flavorButtons = flavorCard;
  const [activeFlavor, setActiveFlavor] = useState(flavorCard[0].flavor || '');
  const imageSizes = getImageSizes({ xxs: '100vw', m: '50vw' });

  return (
    <div className={styles.cardWrapper}>
      <div
        className={styles.cardContainer}
        style={{
          maxWidth: layoutSpacing[bannerWidth as keyof typeof layoutSpacing]
        }}>
        {/* loop over each flavor card */}
        {flavorCard.map((card, index) => {
          const {
            _key,
            flavor,
            ctaText,
            ctaUrl,
            ctaStyle,
            ctaVariant,
            showIcon,
            iconPosition,
            backgroundType,
            backgroundColor,
            backgroundImageDesktop,
            backgroundImageMobile,
            overlayImage,
            overlayImagePosition,
            layoutDirection,
            header,
            subText,
            textColor,
            legalDisclaimer,
            legalDisclaimerColor,
            labelPrefix,
            currentPrice,
            originalPrice,
            priceSuffix,
            disclaimer
          } = card;

          let mutedTextColor = 'muted';

          if (textColor === 'primaryInverse') {
            mutedTextColor = 'mutedLight';
          }

          const backgroundImage = backgroundImageDesktop
            ? urlFor(backgroundImageDesktop).url
            : undefined;
          const backgroundImageMobileUrl = backgroundImageMobile
            ? urlFor(backgroundImageMobile).url
            : undefined;
          const overlayImageUrl = overlayImage ? urlFor(overlayImage).url : undefined;
          const ctaHref = getSanityLinkUrl(ctaUrl, locale);

          let icon = null;
          if (showIcon) {
            if (iconPosition === 'left') {
              icon = <ArrowLeftIcon size="24" />;
            } else if (iconPosition === 'right') {
              icon = <ArrowRightIcon size="24" />;
            }
          }

          const isActive = flavor === activeFlavor;

          return (
            <section
              key={key || `flavor-${flavor}-${index}`}
              className={cardContainer({
                backgroundType,
                layoutDirection
              })}
              style={{
                background:
                  backgroundType === 'color'
                    ? colors.background[backgroundColor as keyof typeof colors.background]
                    : undefined,
                display: isActive ? 'flex' : 'none'
              }}>
              {/* Background image (if selected) */}
              {backgroundType === 'image' && backgroundImage && (
                <div
                  className={styles.backgroundImage}
                  style={
                    {
                      '--bg-image': `url(${backgroundImageMobileUrl})`,
                      '--bg-image-desktop': `url(${backgroundImage})`
                    } as React.CSSProperties
                  }
                />
              )}
              {backgroundType === 'color' && overlayImageUrl && (
                <div className={styles.overlayImageWrapper}>
                  <Image
                    src={overlayImageUrl}
                    alt=""
                    className={clsx(
                      styles.overlayImageBase,
                      styles.overlayImagePosition[overlayImagePosition || 'right']
                    )}
                    aria-hidden="true"
                    width={800}
                    height={800}
                    sizes={imageSizes}
                    priority
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    quality={90}
                  />
                  {legalDisclaimer && (
                    <Typography
                      className={styles.overlayImageDisclaimer}
                      variant="portalCaption"
                      noPadding
                      size="1"
                      style={{
                        color:
                          vars.colors.text[legalDisclaimerColor as keyof typeof vars.colors.text] ||
                          'primaryInverse'
                      }}>
                      <CustomPortableText value={legalDisclaimer} />
                    </Typography>
                  )}
                </div>
              )}

              {card.showTag && (
                <div className={styles.contentTop}>
                  <Tag
                    type={card.tagStyle || 'go-green'}
                    showIcon={card.showTagIcon || false}
                    size={card.tagSize || 's'}>
                    {card.tagText}
                  </Tag>
                </div>
              )}

              <div className={styles.content}>
                <div className={styles.contentBottom}>
                  <div className={styles.contentCopy}>
                    <div>
                      {header && (
                        <Typography
                          variant="headlineSans"
                          noPadding
                          size="2"
                          style={{
                            color:
                              vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                              'primary'
                          }}
                          className={styles.header}
                          dangerouslySetInnerHTML={{ __html: header }}
                        />
                      )}
                      {subText && (
                        <Typography
                          className={styles.bodyText}
                          variant="body"
                          noPadding
                          size="1"
                          style={{
                            color:
                              vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                              'primary'
                          }}>
                          <CustomPortableText value={subText} />
                        </Typography>
                      )}
                    </div>
                    {/* priceDisplay fields */}

                    <div className={styles.priceBlock}>
                      <div className={styles.priceWrapper}>
                        <div className={styles.price}>
                          {labelPrefix && (
                            <Typography
                              component="span"
                              variant="subtitle"
                              weight="700"
                              noPadding
                              size="2"
                              style={{
                                color:
                                  vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                                  'primary'
                              }}>
                              <CustomPortableText value={labelPrefix} />
                            </Typography>
                          )}

                          {currentPrice && (
                            <Typography
                              component="span"
                              variant="subtitle"
                              weight="700"
                              noPadding
                              size="2"
                              style={{
                                color:
                                  vars.colors.text[textColor as keyof typeof vars.colors.text] ||
                                  'primary'
                              }}>
                              <CustomPortableText value={currentPrice} />
                            </Typography>
                          )}

                          {originalPrice && (
                            <Typography
                              component="span"
                              variant="subtitle"
                              noPadding
                              size="2"
                              className={styles.originalPrice}>
                              <CustomPortableText value={originalPrice} />
                            </Typography>
                          )}
                        </div>
                        {priceSuffix && (
                          <Typography
                            component="span"
                            variant="subtitle"
                            style={{
                              color: vars.colors.text.price,
                              marginLeft: vars.spacing.lg
                            }}
                            weight="700"
                            noPadding
                            size="2">
                            <CustomPortableText value={priceSuffix} />
                          </Typography>
                        )}
                      </div>
                      <div>
                        {disclaimer && (
                          <Typography
                            component="span"
                            variant="portalCaption"
                            noPadding
                            size="1"
                            style={{
                              color:
                                vars.colors.text[mutedTextColor as keyof typeof vars.colors.text]
                            }}>
                            <CustomPortableText value={disclaimer} />
                          </Typography>
                        )}
                      </div>
                    </div>

                    <div className={styles.flavorButtonWrapper}>
                      {flavorButtons.map((btn) => (
                        <FlavorBubbleButton
                          key={btn.flavor}
                          isActive={btn.flavor === flavor}
                          activeColor={textColor || 'primaryInverse'}
                          flavor={btn.flavor || 'ag1-original'}
                          onClick={() => setActiveFlavor(btn.flavor || '')}
                        />
                      ))}
                    </div>
                  </div>

                  {ctaText && (
                    <ButtonVanilla
                      className={styles.ctaButton}
                      href={ctaHref}
                      variant={ctaVariant}
                      size="S"
                      shape={ctaStyle || 'round'}
                      iconPosition={iconPosition}
                      icon={icon}>
                      {ctaText}
                    </ButtonVanilla>
                  )}
                </div>
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default PromoFlavorCard;
